import { ref, reactive, watch } from 'vue'
import { useForm, useField } from 'vee-validate'
import { object, number, string, array, date, boolean } from 'yup'
import { usePoliticalActions } from './usePoliticalActions'
import { usePoliticalActionDialog } from './usePoliticalActionDialog'
import { useImgUploader } from '../useImgUploader'
import { useFileUploader } from '../useFileUploader'
import { expenseTypesToArray } from '../useExpenseType'
import { useDialog } from '@/services/useDialog'
import dayjs from 'dayjs'
import * as yup from 'yup'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [''],
  },
  {
    name: '内容',
    required: true,
    status: 0,
    fields: [],
  },
  {
    name: '領収書添付',
    required: true,
    status: 0,
    fields: [],
    disabled: false,
  },
  {
    name: '資料添付',
    required: true,
    status: 0,
    fields: [],
  },
])

const currentMemoryItems = reactive([
  {
    people: undefined,
    type: undefined,
    from: undefined,
    to: undefined,
    detail: undefined,
    stayDays: undefined,
    amount: undefined,
    item: undefined,
    isRoundTrip: undefined,
    distance: undefined,
    expenseTypeId: undefined,
    expenseAmount: undefined,
    expenseDate: undefined,
    expenseContent1: undefined,
    expenseContent2: undefined,
    creditorInfo: undefined,
    haveReceiptImages: undefined,
    isDistributed: undefined,
    numerator: undefined,
    denominator: undefined,
    isPartial: undefined,
    partialAmount: undefined,
    partialComment: undefined,
    images: undefined,
    additionalResources: undefined,
  },
])

const dateComparison = () => {
  if (currentMemoryItems.expenseDate) {
    const inputDate = new Date(currentMemoryItems.expenseDate)
    const nowDate = new Date()
    if (inputDate <= nowDate) {
      return true
    } else {
      return false
    }
  }
}

export const distributedValidation = () => {
  if (
    currentMemoryItems.numerator !== null &&
    currentMemoryItems.denominator !== null
  ) {
    if (currentMemoryItems.numerator < currentMemoryItems.denominator) {
      return true
    } else {
      return false
    }
  }
}

export const amountComparison = () => {
  if (
    currentMemoryItems.isPartial &&
    currentMemoryItems.expenseAmount < currentMemoryItems.partialAmount
  ) {
    return false
  } else {
    return true
  }
}

const schema = object({
  type: array()
    .required('必須です')
    .typeError('必須です')
    .min(1, '必須です')
    .max(1, '必須です'),
  from: string()
    // .required('必須です')
    .typeError('必須です'),
  to: string()
    // .required('必須です')
    .typeError('必須です'),
  people: number()
    // .required('必須です')
    .positive('正の整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください')
    .min(1, '人数には1以上の数を入力してください'),

  detail: string().typeError('詳細を入力してください'),
  stayDays: number()
    // .required('必須です')
    .positive('正の整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください'),
  amount: number()
    // .required('必須です')
    .positive('正の整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください'),
  item: string(),
  isRoundTrip: boolean(),
  distance: number('数値を入力してください').positive(),
  expenseTypeId: array(),
  expenseAmount: number('整数で入力してください')
    .required('必須です')
    .integer('整数で入力してください')
    .typeError('整数で入力してください')
    .positive('正の整数で入力してください'),
  expenseDate: date('必須です')
    .required('必須です')
    .typeError('必須です')
    .test(
      'date_validation',
      '支出年月日には現在時刻より過去の年月日を選択してください',
      dateComparison,
    ),
  expenseContent1: string().required('必須です'),
  expenseContent2: string().nullable(),
  creditorInfo: string().nullable(),
  haveReceiptImages: yup.boolean(),
  isPartial: yup.boolean(),
  partialAmount: number()
    .nullable()
    .when('isPartial', {
      is: true,
      then: number()
        .positive('充当額は正の整数で入力してください')
        .integer('充当額は整数で入力してください')
        .typeError('充当額は整数で入力してください')
        .required('一部充当金額を入力してください')
        .test(
          'amount_validation',
          '充当額には支出額より小さい金額を入力してください',
          amountComparison,
        ),
    }),
  partialComment: yup
    .string()
    .nullable()
    .when('isPartial', {
      is: true,
      then: yup.string().required('一部充当内容を入力してください'),
    }),
  isDistributed: yup.boolean(),
  numerator: number()
    .nullable()
    .when('isDistributed', {
      is: true,
      then: number()
        .nullable()
        .typeError('按分を入力してください')
        .required('按分を入力してください')
        .test(
          'isDistributed_validation',
          '按分率は100%未満になるように入力してください',
          distributedValidation,
        ),
    }),

  denominator: yup.number().nullable(),
})

// TODO: spliceで配列を操作しているが検知できないため（原因特定できず）、暫定的に再描画するためのフラグを設定した。リファクタリング候補
const isShowingMemoryItem = ref(true)

/**
 * @var expense配列のユニークキー
 */
const expenseId = ref(null)

export const useMemoryItemFormTabs = () => {
  return { tabs }
}

export const useMemoryItemForm = () => {
  const { activityDialog } = usePoliticalActionDialog()
  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    if (meta.valid) {
      activityDialog.isSaveMemoryItemForm = true
    } else {
      activityDialog.isSaveMemoryItemForm = false
    }
  })

  const { value: people, errorMessage: peopleError } = useField(
    'people',
    undefined,
    {
      initialValue: currentMemoryItems.people,
    },
  )

  const { value: stayDays, errorMessage: stayDaysError } = useField(
    'stayDays',
    undefined,
    {
      initialValue: currentMemoryItems.stayDays,
    },
  )

  const { value: type, errorMessage: typeError } = useField('type', undefined, {
    initialValue: currentMemoryItems.type,
  })

  const { value: from, errorMessage: fromError } = useField('from', undefined, {
    initialValue: currentMemoryItems.from,
  })

  const { value: to, errorMessage: toError } = useField('to', undefined, {
    initialValue: currentMemoryItems.to,
  })

  const { value: detail, errorMessage: detailError } = useField(
    'detail',
    undefined,
    {
      initialValue: currentMemoryItems.detail,
    },
  )

  const { value: amount, errorMessage: amountError } = useField(
    'amount',
    undefined,
    {
      initialValue: currentMemoryItems.amount,
    },
  )

  const { value: isRoundTrip, errorMessage: isRoundTripError } = useField(
    'isRoundTrip',
    undefined,
    {
      initialValue: currentMemoryItems.isRoundTrip,
    },
  )

  const { value: distance, errorMessage: distanceError } = useField(
    'distance',
    undefined,
    {
      initialValue: currentMemoryItems.distance,
    },
  )
  // item
  const { value: item, errorMessage: itemError } = useField('item', undefined, {
    initialValue: currentMemoryItems.item,
  })

  const {
    value: expenseTypeId,
    errorMessage: expenseTypeIdError,
  } = useField('expenseTypeId', undefined, { initialValue: [] })

  const { value: expenseAmount, errorMessage: expenseAmountError } = useField(
    'expenseAmount',
    undefined,
    {
      initialValue: currentMemoryItems.expenseAmount,
    },
  )

  const { value: expenseDate, errorMessage: expenseDateError } = useField(
    'expenseDate',
    undefined,
    {
      initialValue: currentMemoryItems.expenseDate,
    },
  )

  const {
    value: expenseContent1,
    errorMessage: expenseContent1Error,
  } = useField('expenseContent1', undefined, {
    initialValue: currentMemoryItems.expenseContent1,
  })

  const {
    value: expenseContent2,
    errorMessage: expenseContent2Error,
  } = useField('expenseContent2', undefined, {
    initialValue: currentMemoryItems.expenseContent2,
  })

  const { value: creditorInfo, errorMessage: creditorInfoError } = useField(
    'creditorInfo',
    undefined,
    {
      initialValue: currentMemoryItems.creditorInfo,
    },
  )

  const {
    value: haveReceiptImages,
    errorMessage: haveReceiptImagesError,
  } = useField('haveReceiptImages', undefined, {
    initialValue: currentMemoryItems.haveReceiptImages ?? false,
  })

  const { value: isDistributed, errorMessage: isDistributedError } = useField(
    'isDistributed',
    undefined,
    {
      initialValue: currentMemoryItems.isDistributed,
    },
  )

  const { value: numerator, errorMessage: numeratorError } = useField(
    'numerator',
    undefined,
    {
      initialValue: currentMemoryItems.numerator,
    },
  )

  const { value: denominator, errorMessage: denominatorError } = useField(
    'denominator',
    undefined,
    {
      initialValue: currentMemoryItems.denominator,
    },
  )

  const { value: partialAmount, errorMessage: partialAmountError } = useField(
    'partialAmount',
    undefined,
    {
      initialValue: currentMemoryItems.partialAmount,
    },
  )

  const { value: partialComment, errorMessage: partialCommentError } = useField(
    'partialComment',
    undefined,
    {
      initialValue: currentMemoryItems.partialComment,
    },
  )

  const { value: isPartial, errorMessage: isPartialError } = useField(
    'isPartial',
    undefined,
    {
      initialValue: currentMemoryItems.isPartial,
    },
  )

  const { value: images, errorMessage: imagesError } = useField(
    'images',
    undefined,
    {
      initialValue: currentMemoryItems.image,
    },
  )
  const {
    value: additionalResources,
    errorMessage: additionalResourcesError,
  } = useField('additionalResources', undefined, {
    initialValue: currentMemoryItems.additionalResources,
  })

  const getFromLabel = typeId => {
    switch (typeId) {
      case 1:
        return '出発駅'
      case 2:
        return '出発地'
      case 3:
        return '出発地'
      case 5:
        return '出発地'
      default:
        console.debug('invalid type id: ' + typeId)
    }
    return ''
  }

  const getToLabel = typeId => {
    switch (typeId) {
      case 1:
        return '到着駅'
      case 2:
        return '到着地'
      case 3:
        return '到着地'
      case 5:
        return '到着地'
      default:
        console.debug('invalid type id: ' + typeId)
    }
    return ''
  }

  const initMemoryItemForm = () => {
    // 初期化
    expenseId.value = null
    type.value = []
    from.value = undefined
    to.value = undefined
    people.value = undefined
    detail.value = undefined
    stayDays.value = undefined
    amount.value = undefined
    isRoundTrip.value = false
    distance.value = undefined
    expenseTypeId.value = []
    item.value = undefined
    expenseAmount.value = undefined
    expenseDate.value = undefined
    expenseContent1.value = undefined
    expenseContent2.value = undefined
    creditorInfo.value = undefined
    haveReceiptImages.value = false
    isDistributed.value = false
    numerator.value = undefined
    denominator.value = undefined
    isPartial.value = false
    partialAmount.value = undefined
    partialComment.value = undefined
    images.value = []
    additionalResources.value = []
  }

  const setMemoryItemForm = memoryItem => {
    // 初期化
    initMemoryItemForm()

    // パラメータセット
    expenseId.value = memoryItem?.id ?? null
    type.value = memoryItem?.type ? memoryItem?.type : []
    from.value = memoryItem?.from
    to.value = memoryItem?.to
    isRoundTrip.value = memoryItem?.isRoundTrip ?? false
    people.value = memoryItem?.people
    stayDays.value = memoryItem?.stayDays
    distance.value = memoryItem?.distance
    amount.value = memoryItem?.amount
    detail.value = memoryItem?.detail
    item.value = memoryItem?.item
    // TODO: transportation未記入、追加する
    expenseTypeId.value = memoryItem?.receipt?.expenseTypeId
      ? expenseTypesToArray(memoryItem?.receipt?.expenseTypeId)
      : []
    expenseAmount.value = memoryItem?.receipt?.expenseAmount
    isDistributed.value = memoryItem?.receipt?.isDistributed ?? false
    numerator.value = memoryItem?.receipt?.numerator
    denominator.value = memoryItem?.receipt?.denominator
    isPartial.value = memoryItem?.receipt?.isPartial ?? false
    partialAmount.value = memoryItem?.receipt?.partialAmount
    partialComment.value = memoryItem?.receipt?.partialComment
    expenseDate.value = memoryItem?.receipt?.expenseDate
      ? new Date(memoryItem.receipt.expenseDate)
      : undefined
    expenseContent1.value = memoryItem?.receipt?.expenseContent1
    expenseContent2.value = memoryItem?.receipt?.expenseContent2
    creditorInfo.value = memoryItem?.receipt?.creditorInfo
    haveReceiptImages.value = memoryItem?.receipt?.haveReceiptImages
    images.value = memoryItem?.image ?? []
    const { imgUploader } = useImgUploader()
    imgUploader.imgs = images.value
    additionalResources.value = memoryItem?.additionalResources ?? []
    const { fileUploader } = useFileUploader()
    fileUploader.files = additionalResources.value
  }

  const mergeExpenses = () => {
    isShowingMemoryItem.value = false
    const isAddition = expenseId.value === null
    if (isAddition) {
      addExpenses()
    } else {
      updateExpenses()
    }
    isShowingMemoryItem.value = true
  }

  // 政務活動ダイアログ「入力を確認する」ボタンを押したときにバリデーションを更新する。エラーがなければプレビュー画面へ
  const memorySubmit = handleSubmit(() => {
    const { closeChildDialog } = useDialog()
    mergeExpenses()
    closeChildDialog()
  })

  //入力された値をcurrentMemoryItemsに格納
  watch(people, () => {
    currentMemoryItems.people = people.value
  })
  watch(stayDays, () => {
    currentMemoryItems.stayDays = stayDays.value
  })
  watch(amount, () => {
    currentMemoryItems.amount = amount.value
  })
  watch(
    type,
    () => {
      currentMemoryItems.type = type.value
      setStatus(1)
    },
    { deep: true },
  )
  watch(from, () => {
    currentMemoryItems.from = from.value
  })
  watch(to, () => {
    currentMemoryItems.to = to.value
  })
  watch(detail, () => {
    currentMemoryItems.detail = detail.value
  })
  watch(item, () => {
    currentMemoryItems.item = item.value
  })
  watch(isRoundTrip, () => {
    currentMemoryItems.isRoundTrip = isRoundTrip.value
  })
  watch(distance, () => {
    currentMemoryItems.distance = distance.value
  })
  watch(expenseTypeId, () => {
    currentMemoryItems.expenseTypeId = expenseTypeId.value
  })
  watch(expenseAmount, () => {
    currentMemoryItems.expenseAmount = expenseAmount.value
    setStatus(1)
  })
  watch(expenseDate, () => {
    currentMemoryItems.expenseDate = expenseDate.value
    setStatus(1)
  })
  watch(expenseContent1, () => {
    currentMemoryItems.expenseContent1 = expenseContent1.value
    setStatus(1)
  })
  watch(expenseContent2, () => {
    currentMemoryItems.expenseContent2 = expenseContent2.value
  })
  watch(creditorInfo, () => {
    currentMemoryItems.creditorInfo = creditorInfo.value
  })
  watch(haveReceiptImages, () => {
    currentMemoryItems.haveReceiptImages = haveReceiptImages.value
    if (currentMemoryItems.haveReceiptImages) {
      tabs[2].disabled = true
    } else {
      tabs[2].disabled = false
    }
  })
  watch(isDistributed, () => {
    currentMemoryItems.isDistributed = isDistributed.value
  })
  watch(numerator, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (numerator.value == '') {
      numerator.value = null
      currentMemoryItems.numerator = null
    } else {
      currentMemoryItems.numerator = numerator.value
    }
    setStatus(1)
  })
  watch(denominator, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (denominator.value == '') {
      denominator.value = null
      currentMemoryItems.denominator = null
    } else {
      currentMemoryItems.denominator = denominator.value
    }
    setStatus(1)
  })
  watch(isPartial, () => {
    currentMemoryItems.isPartial = isPartial.value
  })
  watch(partialAmount, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (partialAmount.value == '') {
      partialAmount.value = null
      currentMemoryItems.partialAmount = null
    } else {
      currentMemoryItems.partialAmount = partialAmount.value
    }
    setStatus(1)
  })
  watch(partialComment, () => {
    currentMemoryItems.partialComment = partialComment.value
    setStatus(1)
  })
  watch(images, () => {
    currentMemoryItems.image = images.value
  })
  watch(additionalResources, () => {
    currentMemoryItems.additionalResources = additionalResources.value
  })

  // validateのタイミングでタブのstatusを更新する
  watch(typeError, () => {
    setStatus(0)
  })
  watch(expenseDateError, () => {
    setStatus(1)
  })
  watch(expenseAmountError, () => {
    setStatus(1)
  })
  watch(expenseContent1Error, () => {
    setStatus(1)
  })
  watch(expenseTypeIdError, () => {
    setStatus(1)
  })
  watch(denominatorError, () => {
    setStatus(1)
  })
  watch(numeratorError, () => {
    setStatus(1)
  })

  const setStatus = index => {
    const err = error(index)
    const inp = input(index)
    if (err && inp) {
      tabs[index].status = 1
    } else if (err && !inp) {
      tabs[index].status = 0
    } else if (!err) {
      tabs[index].status = -1
    }
  }

  // エラーがある時falseを返す
  const error = index => {
    let err = false
    if (index === 0) {
      err = typeof typeError.value === 'undefined'
    }
    if (index === 1) {
      err =
        typeof expenseDateError.value === 'undefined' &&
        typeof expenseAmountError.value === 'undefined' &&
        typeof expenseContent1Error.value === 'undefined' &&
        typeof expenseTypeIdError.value === 'undefined' &&
        typeof denominatorError.value === 'undefined' &&
        typeof numeratorError.value === 'undefined'
    }
    return err
  }

  const input = index => {
    let inp = false
    if (index === 0) {
      inp = type.value.length === 1
    }
    if (index === 1) {
      inp = expenseDate.value && expenseAmount.value && expenseContent1.value
    }
    return inp
  }

  /**
   * 記録簿を追加する\
   * FIXME: リファクタリング候補\
   * オブジェクト指向で設計するならばcurrentPoliticalActionを持つクラスが担った方が良い
   */
  const addExpenses = () => {
    const { currentPoliticalAction } = usePoliticalActions()
    const maxId =
      currentPoliticalAction.memory.expenses
        ?.map(expense => expense.id)
        ?.reduce((lId, rId) => Math.max(lId, rId), -2) ?? -1
    const expense = {
      id: maxId + 1,
      type: type.value,
      // from: from.value, //TODO 戸田市では使用しないためコメントアウト
      // to: to.value,
      // isRoundTrip: isRoundTrip.value,
      // people: people.value,
      // distance: distance.value,
      stayDays: stayDays.value,
      // amount: amount.value,
      // detail: detail.value,
      // item: item.value,
      // transportation: type.value, // TODO: 宿泊パックの場圧のパラメーター、追加する
      image: images.value,
      additionalResources: additionalResources.value,
      receipt: {
        expenseTypeId: expenseTypeId.value[0]?.id ?? null,
        expenseAmount: expenseAmount.value,
        isDistributed: isDistributed.value,
        numerator: numerator.value,
        denominator: denominator.value,
        isPartial: isPartial.value,
        partialAmount: partialAmount.value,
        partialComment: partialComment.value,
        expenseDate: dayjs(expenseDate.value).format('YYYY-MM-DD HH:mm:ss'),
        expenseContent1: expenseContent1.value,
        expenseContent2: expenseContent2.value,
        creditorInfo: creditorInfo.value,
        haveReceiptImages: haveReceiptImages.value,
      },
    }
    currentPoliticalAction.memory.expenses.push(expense)
  }

  const updateExpenses = () => {
    const { currentPoliticalAction } = usePoliticalActions()
    currentPoliticalAction.memory.expenses = currentPoliticalAction.memory.expenses.map(
      expense => {
        if (expense.id === expenseId.value) {
          return {
            id: expenseId.value,
            type: type.value,
            from: from.value,
            to: to.value,
            isRoundTrip: isRoundTrip.value,
            people: people.value,
            distance: distance.value,
            stayDays: stayDays.value,
            amount: amount.value,
            detail: detail.value,
            item: item.value,
            // transportation: type.value, // TODO: 宿泊パックの場圧のパラメーター、追加する
            image: images.value,
            additionalResources: additionalResources.value,
            receipt: {
              id: expense.receipt.id,
              expenseTypeId: expenseTypeId.value[0]?.id ?? null,
              expenseAmount: expenseAmount.value,
              isDistributed: isDistributed.value,
              numerator: numerator.value,
              denominator: denominator.value,
              isPartial: isPartial.value,
              partialAmount: partialAmount.value,
              partialComment: partialComment.value,
              expenseDate: dayjs(expenseDate.value).format(
                'YYYY-MM-DD HH:mm:ss',
              ),
              expenseContent1: expenseContent1.value,
              expenseContent2: expenseContent2.value,
              creditorInfo: creditorInfo.value,
              haveReceiptImages: haveReceiptImages.value,
            },
          }
        } else {
          return expense
        }
      },
    )
  }

  /**
   * 記録簿から削除する
   * FIXME: リファクタリング候補\
   * オブジェクト指向で設計するならばcurrentPoliticalActionを持つクラスが担った方が良い
   *
   * @param {object} expense politicalAction.memory.expenses配列内のオブジェクト
   * @returns void
   */
  const removeExpenseFromCurrentPoliticalAction = expense => {
    const { currentPoliticalAction } = usePoliticalActions()
    const expenses = currentPoliticalAction.memory.expenses
    const index = expenses.findIndex(currentExpense => {
      return currentExpense.id === expense.id
    })

    isShowingMemoryItem.value = false
    currentPoliticalAction.memory.expenses.splice(index, 1)
    isShowingMemoryItem.value = true
  }

  const updateImages = () => {
    const { imgUploader } = useImgUploader()
    images.value = imgUploader.imgs
  }

  return {
    expenseId,
    type,
    typeError,
    people,
    peopleError,
    from,
    fromError,
    to,
    toError,
    detail,
    detailError,
    stayDays,
    amount,
    amountError,
    stayDaysError,
    item,
    itemError,
    isRoundTrip,
    isRoundTripError,
    distance,
    distanceError,
    expenseTypeId,
    expenseTypeIdError,
    expenseAmount,
    expenseAmountError,
    expenseDate,
    expenseDateError,
    expenseContent1,
    expenseContent1Error,
    expenseContent2,
    expenseContent2Error,
    haveReceiptImages,
    haveReceiptImagesError,
    creditorInfo,
    creditorInfoError,
    isDistributed,
    isDistributedError,
    numerator,
    numeratorError,
    denominator,
    denominatorError,
    isPartial,
    isPartialError,
    partialAmount,
    partialAmountError,
    partialComment,
    partialCommentError,
    images,
    imagesError,
    additionalResources,
    additionalResourcesError,
    meta,
    isShowingMemoryItem,
    currentMemoryItems,
    expenseTypesToArray,
    getFromLabel,
    getToLabel,
    mergeExpenses,
    removeExpenseFromCurrentPoliticalAction,
    initMemoryItemForm,
    setMemoryItemForm,
    updateImages,
    memorySubmit,
  }
}
